import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { useFormHook } from '../../../hooks/providers/useFormHooks';
import { ISupportedCountry, IUseFormLogic, MoveMoneyTypes, UpdateVendorFormFieldsMap } from '../interfaces';
import { getUpdateVendorFormScheme } from '../schema/form.schema';
import { formFiledList, getDefaultValues } from '../services';
import { useGetSupportedCountries } from '../formBlocks/internationalWireTransfer/hooks/useGetSupportedCountries';
import { useValidateAutoComplete } from '../../../hooks/providers/useValidateAutoComplete';
import { useFormRevalidate } from './useFormRevalidate';
import { isBusinessOrRecipientNameValid } from '../../MoveMoney/utils';

export const useLogic = (props: IUseFormLogic) => {
  const { moveType, formType, initialBusinessName, vendorData, isUpdateNameRequired, isVendorUpdateMandatory } = props;
  const { data: supportedCountries } = useGetSupportedCountries();
  const selectedCountry = useRef<ISupportedCountry | undefined>(undefined);
  const isDomesticAddressRequired = useRef<boolean>(false);
  const isDomesticWireRequired = useRef<boolean>(false);
  const isAchRequired = useRef<boolean>(false);
  const isInternationalAddressRequired = useRef<boolean>(false);
  const isInternationalWireRequired = useRef<boolean>(false);
  const [scheme, setScheme] = useState<z.ZodObject<{}>>(getUpdateVendorFormScheme(moveType, selectedCountry.current, formType, isDomesticAddressRequired.current, isDomesticWireRequired.current, isAchRequired.current, isInternationalAddressRequired.current, isInternationalWireRequired.current));

  useEffect(() => {
    setScheme(getUpdateVendorFormScheme(moveType, selectedCountry.current, formType, isDomesticAddressRequired.current, isDomesticWireRequired.current, isAchRequired.current, isInternationalAddressRequired.current, isInternationalWireRequired.current, isUpdateNameRequired, isVendorUpdateMandatory));
  }, [moveType, selectedCountry.current, formType, isDomesticAddressRequired.current, isDomesticWireRequired.current, isAchRequired.current, isInternationalAddressRequired.current, isInternationalWireRequired.current, isUpdateNameRequired, isVendorUpdateMandatory]);

  const formMethods = useFormHook(
    {
      schemaTitle: 'VendorForm',
      formSchema: scheme,
      formDefaultValues: getDefaultValues({ preFilledBusinessName: initialBusinessName, moveType, vendorData }),
    },
  );

  const { reValidateDomesticWire, reValidateDomesticAddress, reValidateAch, reValidateInternationalWire } = useFormRevalidate(formMethods);

  useValidateAutoComplete({
    formMethods,
    relevantFields: formFiledList,
  });

  const dynamicFormValues = formMethods.watch();

  useEffect(() => {
    const name = formMethods.getValues(UpdateVendorFormFieldsMap.BUSINESS_NAME);
    if (name && !isBusinessOrRecipientNameValid(name)) {
      formMethods.trigger(UpdateVendorFormFieldsMap.BUSINESS_NAME);
    }
  }, []);

  useEffect(() => {
    const { line1,
      line2,
      city,
      state,
      postalCode,
      interCountry,
      wireAccountNumber,
      wireRoutingNumber,
      achAccountNumber,
      achRoutingNumber,
      interLine1,
      interLine2,
      interCity,
      interPostalCode,
      interState,
      swift,
      iban,
      interPhone,
    } = dynamicFormValues;
    const domesticAddressValues = [line1, line2, city, state, postalCode];
    const domesticWireValues = [wireAccountNumber, wireRoutingNumber];
    const achValues = [achRoutingNumber, achAccountNumber];
    const internationalWireValues = [interLine1, interLine2, interCity, interPostalCode, interState, swift, iban, interPhone, interCountry];

    /** Update form scheme dynamically */
    selectedCountry.current = supportedCountries?.supportedCountries.countries.find((country) => country.code === interCountry);
    isDomesticAddressRequired.current = moveType === MoveMoneyTypes.WIRE_OUT || moveType === MoveMoneyTypes.MAIL_CHECK || domesticAddressValues.some((value) => !!value);
    isDomesticWireRequired.current = moveType === MoveMoneyTypes.WIRE_OUT || domesticWireValues.some((value) => !!value);
    isAchRequired.current = moveType === MoveMoneyTypes.ACH_OUT_TRANSFER || achValues.some((value) => !!value);
    isInternationalAddressRequired.current = moveType === MoveMoneyTypes.INTERNATIONAL_WIRE_OUT || internationalWireValues.some((value) => !!value);
    isInternationalWireRequired.current = moveType === MoveMoneyTypes.INTERNATIONAL_WIRE_OUT || internationalWireValues.some((value) => !!value);

    if (!isDomesticAddressRequired.current) reValidateDomesticAddress();
    if (!isDomesticWireRequired.current) reValidateDomesticWire();
    if (!isAchRequired.current) reValidateAch();
    if (!isInternationalAddressRequired.current && !isInternationalWireRequired.current) reValidateInternationalWire();
  }, [dynamicFormValues]);

  useEffect(() => {
    if (formMethods.getFieldState(UpdateVendorFormFieldsMap.BUSINESS_NAME).isTouched) {
      formMethods.trigger(UpdateVendorFormFieldsMap.BUSINESS_NAME);
    }
  }, [formType, scheme]);

  return { formMethods, scheme };
};
